import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { hydratedGlobalParams } from '../../../shared/workspace-element-context';
import { JsonObjectFactoryService } from '../../../global/services/json-object-factory.service';

/**
 * HTTP shim for the three scrubbing-related actions defined in the legacy
 * `ui/app/components/efile/EfileService.js` / `efile.js`. Each action is a
 * form-urlencoded `POST /saveJsonObject?action_code=...` call against the
 * same base url used throughout the efile module.
 *
 * Callers should obtain `baseUrl` via `generalConfigFromGlobal(...).base_url`
 * (the Angular 12 equivalent of `GENERAL_CONFIG.base_url`).
 */
export interface EnableDisableScrubbingPayload {
  submission_id: string | number;
  scrubbing_id: string; // csv of SCRUBBING_IDs, mirroring efile.js L257
  tax_year: string | number;
  scenario: string | number;
  jcd_key: number; // legacy hard-pins this to 250
  sso_id: string;
}

export interface DisableDuplicateSnoPayload {
  submission_id: string | number;
  clob_data: string; // JSON.stringify(rows.map(r => r.data))
  sso_id: string;
}

export interface ResolveScrubbingsForSubPayload {
  submission_id: string | number;
  clob_data: string;
  sso_id: string;
}

/**
 * Payload for the search→replace bulk apply that the legacy
 * `efile-scrubbing.run` and `efile-scrubbing.history` tabs trigger via
 * the `searchKeyToReplaceKey` ctrlFn (efile.js L457-498 / L556-597).
 *
 * `search_key`/`replace_key` are CSVs harvested from the filter bar's
 * selected scrubbing entries; `formKey` is only present on the run tab
 * (the history tab's filter bar has no Form selector).
 */
export interface SearchKeyToReplaceKeyPayload {
  submission_id: string | number;
  tax_year: string | number;
  scenario: string | number;
  jcd_key: number;
  sso_id: string;
  search_key: string;
  replace_key: string;
  formKey?: string;
}

@Injectable({ providedIn: 'root' })
export class EfileScrubbingActionsService {
  /** Legacy action_code mappings. Do not change without a backend contract change. */
  private static readonly ACTION_CODES = {
    enableDisableScrubbing: 'kjkq9s',
    disableDuplicateSno: 'ywwilf',
    resolveScrubbingsForSub: 'o2j5pd',
    /** searchKey → replaceKey bulk apply (efile.js L478, run + history). */
    searchKeyToReplaceKey: 'ck1uzf',
    /** Filter loader: forms by submission (efile.js L409, run tab). */
    formsBySubmission: 'oisavn',
    /** Filter loader: scrubbing list (efile.js L434/L535, run + history). */
    scrubbingListLookup: 'sjrs2c',
  } as const;

  constructor(
    private http: HttpClient,
    private globalService: GlobalService,
    private workspaceService: WorkspaceService,
    private jsonFactory: JsonObjectFactoryService,
  ) {}

  /**
   * Full workspace-hydrated global params (tax_year, scenario, jcd_key,
   * screen_key, activity_key, jcd_ta_key, client_key context, etc.). This is
   * the same envelope the efile grid forwards via `resolveCustomScreenContext`
   * + `globalParams`. The raw `GlobalService.global.globalParams` defaults to
   * empty strings on custom efile screens, so the filter-loader SPs
   * (`oisavn` / `sjrs2c`) returned no rows — leaving the run-tab dropdowns
   * empty — until the active-screen filter context was merged in.
   */
  private resolveGlobalParams(): Record<string, any> {
    return hydratedGlobalParams(this.globalService, this.workspaceService) || {};
  }

  /** Mirrors `ctrlFn('enableDisableScrubbing', ...)` in efile.js (~L253-272). */
  enableDisableScrubbing(
    baseUrl: string,
    payload: EnableDisableScrubbingPayload,
  ): Observable<any> {
    return this.postForm(
      baseUrl,
      EfileScrubbingActionsService.ACTION_CODES.enableDisableScrubbing,
      payload,
    );
  }

  /** Mirrors `EfileFactory.disableDuplicateSno` in EfileService.js (~L686-697). */
  disableDuplicateSno(
    baseUrl: string,
    payload: DisableDuplicateSnoPayload,
  ): Observable<any> {
    return this.postForm(
      baseUrl,
      EfileScrubbingActionsService.ACTION_CODES.disableDuplicateSno,
      payload,
    );
  }

  /** Mirrors `EfileFactory.resolveScrubbingsForSub` in EfileService.js (~L699-710). */
  resolveScrubbingsForSub(
    baseUrl: string,
    payload: ResolveScrubbingsForSubPayload,
  ): Observable<any> {
    return this.postForm(
      baseUrl,
      EfileScrubbingActionsService.ACTION_CODES.resolveScrubbingsForSub,
      payload,
    );
  }

  /**
   * Mirrors the `doReplace(true, data)` save in efile.js L476-496 used by
   * the run + history tabs' `searchKeyToReplaceKey` ctrlFn. The `formKey`
   * field is omitted on the history tab (legacy doesn't send it either).
   */
  searchKeyToReplaceKey(
    baseUrl: string,
    payload: SearchKeyToReplaceKeyPayload,
  ): Observable<any> {
    return this.postForm(
      baseUrl,
      EfileScrubbingActionsService.ACTION_CODES.searchKeyToReplaceKey,
      payload,
    );
  }

  /**
   * Loads "Form By Submission" lookup options for the run tab's filter
   * bar (efile.js L409). Returns rows whose `FORM` column is used both as
   * label and value in the legacy filter contract.
   *
   * Mirrors the legacy `df-select-multiple` loader contract: a GET against
   * `/loadJsonObject?action_code=oisavn` with the full
   * `GlobalService.global.globalParams` envelope (jcd_key, scenario,
   * tax_year, screen_key, screen_type, jcd_ta_key, activity_key,
   * is_locked, issue_id, etc.) merged with `submission_id` + `sso_id`.
   * Sending only `submission_id`/`sso_id` returns an empty array because
   * the SP is filtered by the workspace context the legacy data-filters
   * loader always shipped.
   */
  loadFormsBySubmission(
    baseUrl: string,
    submissionId: string | number,
    taxYear?: string | number,
    fresh = false,
  ): Observable<any> {
    const globalParams = this.resolveGlobalParams();
    return this.getJson(
      baseUrl,
      EfileScrubbingActionsService.ACTION_CODES.formsBySubmission,
      {
        activity_key: globalParams.activity_key ?? '',
        is_issue_key_enabled: globalParams.is_issue_key_enabled ?? 'N',
        is_locked: globalParams.is_locked ?? 'N',
        issue_id: globalParams.issue_id ?? 'issue_id_for_tx_save',
        issue_short_desc: globalParams.issue_short_desc ?? '',
        jcd_key: 250,
        jcd_ta_key: globalParams.jcd_ta_key ?? '',
        scenario: globalParams.scenario ?? '',
        scenarioCode: globalParams.scenarioCode ?? 'RAF',
        screen_key: globalParams.screen_key ?? '',
        screen_type: globalParams.screen_type ?? 'custom',
        submission_id: submissionId,
        tax_year: taxYear ?? globalParams.tax_year ?? '',
      },
      fresh,
    );
  }

  /**
   * Loads "Scrubbing list" lookup options for the run + history tab
   * filter bars (efile.js L434/L535). Each row carries SEARCH_KEY and
   * REPLACE_KEY columns; the run tab uses both, the history tab swaps
   * them when invoking searchKeyToReplaceKey (legacy L585-586).
   *
   * See {@link loadFormsBySubmission} for the global-params merge
   * rationale — same contract.
   */
  loadScrubbingListLookup(
    baseUrl: string,
    submissionId: string | number,
    fresh = false,
  ): Observable<any> {
    return this.getJson(
      baseUrl,
      EfileScrubbingActionsService.ACTION_CODES.scrubbingListLookup,
      { submission_id: submissionId },
      fresh,
    );
  }

  /**
   * Generic urlencoded POST helper. Defaults to `saveJsonObject` to keep
   * the historical save call sites unchanged; pass `'loadJsonObject'` for
   * the filter-loader endpoints.
   */
  private postForm(
    baseUrl: string,
    actionCode: string,
    body: Record<string, any>,
    endpoint: 'saveJsonObject' | 'loadJsonObject' = 'saveJsonObject',
  ): Observable<any> {
    const url = `${baseUrl}/${endpoint}?action_code=${actionCode}`;
    const params = new URLSearchParams();
    Object.keys(body).forEach((key) => {
      const value = body[key];
      if (value !== null && value !== undefined) {
        params.set(key, String(value));
      }
    });
    return this.http.post<any>(url, params.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      withCredentials: true,
    });
  }

  /**
   * GET helper for filter-loader endpoints. Mirrors the legacy
   * `DataFiltersService.loadFilterValues` flow: `GlobalService` global
   * params merge with caller-supplied extras, sent as query string.
   * Empty / null values are dropped to avoid `param=null` artifacts.
   */
  private getJson(
    baseUrl: string,
    actionCode: string,
    extras: Record<string, any>,
    fresh = false,
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    const globalParams = this.resolveGlobalParams();
    const merged: Record<string, any> = {
      ...globalParams,
      ...extras,
      action_code: actionCode,
    };

    let params = new HttpParams();
    Object.keys(merged)
      .sort()
      .forEach((key) => {
        const value = merged[key];
        if (value === undefined || value === null) return;
        params = params.set(key, String(value));
      });

    const requestKey = `${url}?${params.toString()}`;
    return this.jsonFactory.shareGet(requestKey, fresh, () =>
      this.http.get<any>(url, { params, withCredentials: true }),
    );
  }
}
